/**
 * useAssetsReady
 *
 * Resolves to `true` once the visual assets needed to render the next page
 * are paint-ready: the brand font (Inter) and the workspace logo
 * bitmap (when one is known). Used to gate the join → interview transition
 * so we never reveal a conversation whose text reflows when fonts arrive late or
 * whose header logo pops in after the layout settles.
 *
 * Concurrent waits - fonts and image decode happen in parallel, not series.
 * A hard 3s timeout guards against a slow CDN: if an asset is taking that
 * long, the user is better off seeing the conversation than staring at the loader.
 *
 * The font wait relies on `document.fonts.ready`, which only resolves once
 * fonts that are *in use* on the page have loaded. `BrandedLineLoader`
 * mounts a hidden 1px Inter element so the browser starts the font
 * download as soon as the loader appears - without that trigger, the font
 * wouldn't be "in use" and `fonts.ready` would resolve instantly while the
 * font itself is still missing.
 *
 * @module lib/useAssetsReady
 */

import { useEffect, useState } from 'react';

const ASSET_TIMEOUT_MS = 3000;

/**
 * Wait for fonts + the workspace logo image to be paint-ready before
 * letting the caller dismiss its loader.
 *
 * @param workspaceLogoUrl - Wide-logo URL for the active workspace.
 *   `null`/`undefined` means no workspace logo to preload (we still wait
 *   on fonts).
 * @returns `true` once the assets are ready (or the 3s timeout fires).
 */
export function useAssetsReady(
  workspaceLogoUrl: string | null | undefined,
): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tasks: Promise<unknown>[] = [];

    if (typeof document !== 'undefined' && 'fonts' in document) {
      tasks.push(document.fonts.ready);
    }

    if (workspaceLogoUrl && typeof Image !== 'undefined') {
      const img = new Image();
      img.src = workspaceLogoUrl;
      // `decode()` resolves once the image is decoded and ready to paint.
      // If decode is unsupported or the URL fails, fall back to onload so
      // we never hang. Either way, swallow errors.
      const decodeTask = typeof img.decode === 'function'
        ? img.decode().catch(() => undefined)
        : new Promise<void>((res) => {
            img.onload = () => res();
            img.onerror = () => res();
          });
      tasks.push(decodeTask);
    }

    const timeout = new Promise<void>((res) =>
      window.setTimeout(res, ASSET_TIMEOUT_MS),
    );

    void Promise.race([Promise.all(tasks), timeout]).then(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [workspaceLogoUrl]);

  return ready;
}
